import { useState, type ReactNode } from "react";

import {
  useCreateShare,
  useRevokeShare,
  useShareInfoFor,
  useUpdateShare,
  validateUrlId,
  type Share,
  type ShareSettings,
  type ShareTarget,
} from "../api/sharing";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { TextField } from "../ui/Field";
import { Toggle } from "../ui/Toggle";
import { useToast } from "../ui/toast-context";
import { DocumentAccessList } from "./DocumentAccessList";

/**
 * The share dialog (`docs/spec/shares.md` §2, §4).
 *
 * Two halves. The public link is the share record: whether it is published, what
 * the public page shows, and an optional custom slug. Direct access is the
 * document's own memberships — who was granted it by name, and with what
 * permission — and is offered only for a document, since a collection has its
 * own permissions dialog.
 *
 * Both halves are gated by the caller's abilities and by the workspace and
 * collection sharing switches, which are ANDed above the share's own `published`
 * flag: an admin turning sharing off kills every link without losing any
 * configuration.
 */
export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  /** The resource's name, shown under the dialog title. */
  title: string;
  target: ShareTarget;
  /** The `share` ability on the resource. */
  canShare: boolean;
  /** The workspace and collection switches, already ANDed by the caller. */
  sharingEnabled: boolean;
  /** Whether direct grants may be changed (`documents.add_user` and friends). */
  canManageAccess?: boolean;
}

export function ShareDialog({
  open,
  onClose,
  title,
  target,
  canShare,
  sharingEnabled,
  canManageAccess = false,
}: ShareDialogProps): ReactNode {
  const toast = useToast();
  const info = useShareInfoFor(target, open && canShare && sharingEnabled);
  const create = useCreateShare();
  const update = useUpdateShare();
  const revoke = useRevokeShare();
  const [slug, setSlug] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | undefined>(undefined);

  const shares = info.data?.shares ?? [];
  const own =
    shares.find((share) =>
      target.documentId === undefined
        ? share.collectionId === target.collectionId
        : share.documentId === target.documentId,
    ) ?? null;
  // An ancestor's share with `includeChildDocuments` already exposes this page.
  const inherited = shares.find((share) => share !== own) ?? null;

  const report = (error: Error): void => {
    toast.show(error.message, "error");
  };

  const change = (settings: ShareSettings): void => {
    if (own === null) {
      create.mutate({ ...target, ...settings }, { onError: report });
      return;
    }
    update.mutate({ ...target, id: own.id, ...settings }, { onError: report });
  };

  const saveSlug = (): void => {
    const value = (slug ?? "").trim().toLowerCase();
    const problem = validateUrlId(value);
    setSlugError(problem ?? undefined);
    if (problem !== null || own === null) return;
    update.mutate(
      { ...target, id: own.id, urlId: value === "" ? null : value },
      {
        onSuccess: () => {
          setSlug(null);
          toast.show("Link updated");
        },
        onError: report,
      },
    );
  };

  const copyLink = (share: Share): void => {
    void navigator.clipboard
      .writeText(share.url)
      .then(() => {
        toast.show("Link copied");
      })
      .catch(() => {
        toast.show("The link could not be copied", "error");
      });
  };

  return (
    <Dialog open={open} onClose={onClose} title="Share" description={title} width="md">
      {canShare && sharingEnabled ? (
        <section aria-label="Public link" className="flex flex-col gap-2">
          <Toggle
            label="Publish to the web"
            description="Anyone with the link can read this page, without signing in."
            checked={own?.published === true}
            disabled={create.isPending || update.isPending}
            onChange={(published) => {
              change({ published });
            }}
          />

          {own !== null && own.published && (
            <>
              {target.documentId !== undefined && (
                <Toggle
                  label="Include nested documents"
                  description="Published pages below this one are part of the same link."
                  checked={own.includeChildDocuments}
                  onChange={(includeChildDocuments) => {
                    change({ includeChildDocuments });
                  }}
                />
              )}
              <Toggle
                label="Allow search engines"
                checked={own.allowIndexing}
                onChange={(allowIndexing) => {
                  change({ allowIndexing });
                }}
              />
              <Toggle
                label="Show table of contents"
                checked={own.showTOC}
                onChange={(showTOC) => {
                  change({ showTOC });
                }}
              />
              <Toggle
                label="Show last updated"
                checked={own.showLastUpdated}
                onChange={(showLastUpdated) => {
                  change({ showLastUpdated });
                }}
              />

              <TextField
                label="Custom link"
                description="Lower-case letters, digits and hyphens, 4 to 32 characters."
                value={slug ?? own.urlId ?? ""}
                error={slugError}
                onChange={(value) => {
                  setSlug(value);
                  setSlugError(undefined);
                }}
              />

              <div className="flex flex-wrap items-center gap-2">
                <code className="truncate rounded bg-hover px-2 py-1 text-xs">{own.url}</code>
                <Button
                  size="sm"
                  onClick={() => {
                    copyLink(own);
                  }}
                >
                  Copy link
                </Button>
                <Button size="sm" disabled={slug === null} onClick={saveSlug}>
                  Save link
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={revoke.isPending}
                  onClick={() => {
                    revoke.mutate(
                      { ...target, id: own.id },
                      {
                        onSuccess: () => {
                          toast.show("Link revoked");
                        },
                        onError: report,
                      },
                    );
                  }}
                >
                  Revoke
                </Button>
              </div>
            </>
          )}

          {inherited !== null && (
            <p className="text-xs text-muted">
              This page is also public through a parent page: {inherited.url}
            </p>
          )}
        </section>
      ) : (
        <p className="text-sm text-muted">
          {canShare
            ? "Public sharing is turned off for this workspace or collection."
            : "You cannot create a public link for this page."}
        </p>
      )}

      {target.documentId !== undefined && (
        <DocumentAccessList
          documentId={target.documentId}
          enabled={open}
          canManage={canManageAccess}
        />
      )}

      <div className="mt-5 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </Dialog>
  );
}
